import { Directive, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';
import { TranslateService } from '@interticket/core';
import { IValidatorErrorMessage } from '@shared/interfaces/forms/validator-error-message.interface';
import UtilForm from '@shared/utilities/util-form.utility';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[appShowErrors]',
  exportAs: 'appShowErrors',
})
export class AppShowErrorsDirective implements OnInit, OnDestroy {

  static readonly INPUT_HINT_CLASS = 'form-input-hint';
  public static readonly INVALID_INPUT_CLASS = 'is-error';
  public static readonly SHOW_ERRORS_CLASS = 'app-show-error-show';

  private static readonly errorMessages = {
    /**
     * For default Angular validators
     * {@link} https://angular.io/api/forms/Validators
     */
    required: () => ({ label: 'label_field_is_required' }),
    email: () => ({ label: 'ss_lang_invalid_email' }),
    min: (params: any) => ({ label: 'label_min_value', params: [params.min] }),
    max: (params: any) => ({ label: 'label_max_value', params: [params.max] }),
    minlength: (params: any) => ({ label: 'label_validation_min_length', params: [params.requiredLength] }),
    maxlength: (params: any) => ({ label: 'label_validation_max_length', params: [params.requiredLength] }),
    pattern: (params: any) => ({ label: 'label_validation_pattern', params: [params.requiredPattern] }),
    emailConflict: () => ({ label: 'email_already_registered_label' }),

    /**
     * For DateTimePicker validators
     * {@link} https://danielykpan.github.io/date-time-picker/
     */
    owlDateTimeParse: () => ({ label: 'label_validation_datetime_parse' }),
    owlDateTimeMin: (params: any) => ({ label: 'label_validation_datetime_min', params: [params.min.toFormat('D')] }),
    owlDateTimeMax: (params: any) => ({ label: 'label_validation_datetime_max', params: [params.max.toFormat('D')] }),
    owlDateTimeFilter: () => ({ label: 'label_validation_datetime_filter' }),
    owlDateTimeRange: () => ({ label: 'label_validation_datetime_range' }),

    /**
     * For custom app validators
     * */
    custom: (params: any) => params,
  };

  private readonly destroy$ = new Subject<void>();

  showHint: boolean;

  @Input() readonly control: NgControl;
  @Input() readonly customMessage: string;

  constructor(
    private errorField: ElementRef,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.control.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.setErrorMessage(),
      });

    this.errorField.nativeElement.classList['add'](AppShowErrorsDirective.INPUT_HINT_CLASS);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setErrorMessage(): void {
    if (UtilForm.isInvalidControl(this.control) && this.control.errors) {
      this.showErrorMessage();
    } else {
      this.hideErrorMessage();
    }
  }

  private showErrorMessage() {
    this.showHint = true;

    const errorMessage: IValidatorErrorMessage = this.getFirstErrorMessage();
    this.errorField.nativeElement.innerHTML = this.translate.get(errorMessage.label, errorMessage.params);
    this.errorField.nativeElement.classList['add'](AppShowErrorsDirective.SHOW_ERRORS_CLASS);
  }

  private hideErrorMessage() {
    this.showHint = false;
    this.errorField.nativeElement.classList['remove'](AppShowErrorsDirective.SHOW_ERRORS_CLASS);

    setTimeout(() => {
      if (!this.showHint) {
        this.errorField.nativeElement.innerHTML = null;
      }
    }, 250);
  }

  private getFirstErrorMessage(): IValidatorErrorMessage {
    return Object.keys(this.control.errors)
      .map(field => this.getMessage(field, this.control.errors[field]))
      .shift();
  }

  private getMessage(type: string, params: any): IValidatorErrorMessage {
    if (this.customMessage) {
      return { label: this.customMessage };
    }
    const messageType: string = AppShowErrorsDirective.errorMessages[type] ? type : 'custom';
    return AppShowErrorsDirective.errorMessages[messageType](params);
  }

}
